import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useCallback } from 'react';

export const MAX_COMPARE_ITEMS = 4;

export type CompareSelectionType = 'figma' | 'vibe';

export type CompareSelectionItem = {
  id: string;
  label: string;
  subtitle?: string;
};

type CompareSelectionsByType = Record<
  CompareSelectionType,
  CompareSelectionItem[]
>;

const emptySelections = (): CompareSelectionsByType => ({
  figma: [],
  vibe: [],
});

const trimItems = (items: CompareSelectionItem[]) =>
  items.slice(0, MAX_COMPARE_ITEMS);

const areItemsEqual = (
  left: CompareSelectionItem[],
  right: CompareSelectionItem[]
) =>
  left.length === right.length &&
  left.every(
    (item, index) =>
      item.id === right[index]?.id &&
      item.label === right[index]?.label &&
      item.subtitle === right[index]?.subtitle
  );

export type CompareSelectionStore = {
  teamId: string | null;
  workspaceId: string | null;
  selections: CompareSelectionsByType;
  ensureScope: (teamId: string, workspaceId: string) => void;
  add: (type: CompareSelectionType, item: CompareSelectionItem) => void;
  remove: (type: CompareSelectionType, id: string) => void;
  clear: (type: CompareSelectionType) => void;
  clearAll: () => void;
  setItems: (
    type: CompareSelectionType,
    items: CompareSelectionItem[],
    teamId: string,
    workspaceId: string
  ) => void;
  getItems: (type: CompareSelectionType) => CompareSelectionItem[];
  isSelected: (type: CompareSelectionType, id: string) => boolean;
  canAdd: (type: CompareSelectionType) => boolean;
};

export const useCompareSelectionStore = create(
  persist<CompareSelectionStore>(
    (set, get) => ({
      teamId: null,
      workspaceId: null,
      selections: emptySelections(),
      ensureScope: (teamId, workspaceId) => {
        const state = get();

        if (state.teamId === teamId && state.workspaceId === workspaceId) {
          const { figma, vibe } = state.selections;

          if (
            figma.length > MAX_COMPARE_ITEMS ||
            vibe.length > MAX_COMPARE_ITEMS
          ) {
            set({
              selections: {
                figma: trimItems(figma),
                vibe: trimItems(vibe),
              },
            });
          }

          return;
        }

        set({
          teamId,
          workspaceId,
          selections: emptySelections(),
        });
      },
      add: (type, item) => {
        const state = get();
        const current = state.selections[type];

        if (current.some((existing) => existing.id === item.id)) {
          return;
        }

        if (current.length >= MAX_COMPARE_ITEMS) {
          return;
        }

        set({
          selections: {
            ...state.selections,
            [type]: [...current, item],
          },
        });
      },
      remove: (type, id) => {
        const state = get();

        set({
          selections: {
            ...state.selections,
            [type]: state.selections[type].filter((item) => item.id !== id),
          },
        });
      },
      clear: (type) => {
        const state = get();

        set({
          selections: {
            ...state.selections,
            [type]: [],
          },
        });
      },
      clearAll: () => {
        set({
          selections: emptySelections(),
        });
      },
      setItems: (type, items, teamId, workspaceId) => {
        const state = get();
        const nextItems = trimItems(items);

        if (
          state.teamId === teamId &&
          state.workspaceId === workspaceId &&
          areItemsEqual(state.selections[type], nextItems)
        ) {
          return;
        }

        set({
          teamId,
          workspaceId,
          selections: {
            ...state.selections,
            [type]: nextItems,
          },
        });
      },
      getItems: (type) => get().selections[type],
      isSelected: (type, id) =>
        get().selections[type].some((item) => item.id === id),
      canAdd: (type) => get().selections[type].length < MAX_COMPARE_ITEMS,
    }),
    {
      name: 'compare-selection',
      version: 3,
      migrate: (persistedState) => {
        const state = persistedState as Partial<CompareSelectionStore> & {
          type?: CompareSelectionType | null;
          items?: CompareSelectionItem[];
        };

        if (state.selections?.figma || state.selections?.vibe) {
          return {
            ...state,
            teamId: state.teamId ?? null,
            workspaceId: state.workspaceId ?? null,
            selections: {
              figma: trimItems(state.selections?.figma || []),
              vibe: trimItems(state.selections?.vibe || []),
            },
          } as CompareSelectionStore;
        }

        const legacyType = state.type || null;
        const legacyItems = trimItems(state.items || []);
        const selections = emptySelections();

        if (legacyType && legacyItems.length) {
          selections[legacyType] = legacyItems;
        }

        return {
          ...state,
          teamId: state.teamId ?? null,
          workspaceId: state.workspaceId ?? null,
          selections,
        } as CompareSelectionStore;
      },
    }
  )
);

export const useCompareSelection = (type: CompareSelectionType) => {
  const teamId = useCompareSelectionStore((state) => state.teamId);
  const workspaceId = useCompareSelectionStore((state) => state.workspaceId);
  const items = useCompareSelectionStore((state) => state.selections[type]);
  const ensureScope = useCompareSelectionStore((state) => state.ensureScope);
  const addToStore = useCompareSelectionStore((state) => state.add);
  const removeFromStore = useCompareSelectionStore((state) => state.remove);
  const clearFromStore = useCompareSelectionStore((state) => state.clear);
  const setItemsInStore = useCompareSelectionStore((state) => state.setItems);
  const canAdd = useCompareSelectionStore(
    (state) => state.selections[type].length < MAX_COMPARE_ITEMS
  );

  const add = useCallback(
    (item: CompareSelectionItem) => addToStore(type, item),
    [addToStore, type]
  );
  const remove = useCallback(
    (id: string) => removeFromStore(type, id),
    [removeFromStore, type]
  );
  const clear = useCallback(() => clearFromStore(type), [clearFromStore, type]);
  const setItems = useCallback(
    (
      nextItems: CompareSelectionItem[],
      nextTeamId: string,
      nextWorkspaceId: string
    ) => setItemsInStore(type, nextItems, nextTeamId, nextWorkspaceId),
    [setItemsInStore, type]
  );
  const isSelected = useCallback(
    (id: string) => items.some((item) => item.id === id),
    [items]
  );

  return {
    type,
    teamId,
    workspaceId,
    items,
    ensureScope,
    canAdd,
    add,
    remove,
    clear,
    setItems,
    isSelected,
  };
};
